// FollowupsView — "Seguimiento / Envíos". Tracks two kinds of outbound work,
// AUTO-FED from data that already exists so there's almost nothing to type
// (Lissette 2026-06-23: "quiero que sea lo más automatizado"):
//
//   🎁 Gift Cards  → auto-filled from REFERRALS in the system (every client
//      who has a referrer). You just mark the reward status
//      (Pendiente→Enviado→Reenviado→Entregado). Manual rows still allowed.
//
//   📄 Documents   → auto-filled from DOCUMENT REQUESTS. "Recibido del cliente"
//      is automatic (the request turns 'completed' when the client uploads) or
//      you can mark it by hand; the "Enviado al Marketplace/compañía" part is
//      manual. Manual rows still allowed.
//
// Auto rows are virtual (derived from `clients` / `requests`). The moment you
// set a status (or any manual field) on an auto row, a `followups` record is
// created linked to its source via (sourceType, sourceId), so the auto row and
// its saved state MERGE — no duplicates. Pure-manual rows have no source.
//
// Persistence is delegated to the App via `onFollowupsChange(prev => next)`,
// which diffs and fires create/update/delete (same pattern as eligibility).

import { useState, useMemo, useEffect } from 'react';
import { Gift, FileText, Plus, Trash2, Pencil, AlertTriangle, Send, Check, Zap, Flag, Columns as ColumnsIcon } from 'lucide-react';
import { Modal, ClientSearchSelect, confirmDialog, NoteEditor, RichNote } from './ui.jsx';
import CustomColumnsManager from './CustomColumnsManager.jsx';
import { estaMarcada, normalizeColumns, opcionesDeColumna, tipoDeColumna } from '../customColumns.js';
import { CampoDeColumnaPropia } from './CeldaPropia.jsx';
import { getAgencySettings as apiGetAgencySettings, updateAgencySettings as apiUpdateAgencySettings } from '../api.js';
import { toast } from './Toast.jsx';
import { guardarColumnasDeAjuste } from '../columnasDeTabla.js';

const DAY = 86400000;

// ── Agency-defined custom statuses + columns (shared, cached at module scope) ──
// Same lifecycle as the Tasks/Tickets custom statuses: loaded once from
// AgencySetting and persisted back via updateAgencySettings.
let _fuStatusCache = null;
function useFollowupStatuses() {
  const [custom, setCustom] = useState(_fuStatusCache || []);
  useEffect(() => {
    const isApi = typeof window !== 'undefined' && !!window.__deskteAuth;
    if (!isApi) return;
    let alive = true;
    apiGetAgencySettings().then((s) => {
      if (!alive) return;
      const list = Array.isArray(s?.followupStatuses) ? s.followupStatuses : [];
      _fuStatusCache = list; setCustom(list);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const persist = async (list) => {
    _fuStatusCache = list; setCustom(list);
    const isApi = typeof window !== 'undefined' && !!window.__deskteAuth;
    if (isApi) { try { await apiUpdateAgencySettings({ followupStatuses: list }); } catch (e) { toast.error('No se pudo guardar el estado: ' + e.message); } }
  };
  return [custom, persist];
}
let _fuColCache = null;
function useFollowupColumns() {
  const [cols, setCols] = useState(_fuColCache || []);
  useEffect(() => {
    const isApi = typeof window !== 'undefined' && !!window.__deskteAuth;
    if (!isApi) return;
    let alive = true;
    apiGetAgencySettings().then((s) => {
      if (!alive) return;
      const list = normalizeColumns(Array.isArray(s?.followupCustomColumns) ? s.followupCustomColumns : []);
      _fuColCache = list; setCols(list);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  // Solo se borra lo que se borró en la ventana (ticket de Marilyn Sequera,
  // 2026-10-28). Ver crm-app/columnasDeTabla.js.
  // Si no se guarda, la ventana de columnas enseña el error y no se cierra.
  const persist = async (list) => {
    const isApi = typeof window !== 'undefined' && !!window.__deskteAuth;
    const final = isApi
      ? await guardarColumnasDeAjuste('followupCustomColumns', cols, list)
      : normalizeColumns(list);
    _fuColCache = final; setCols(final);
  };
  return [cols, persist];
}

const STATUS = {
  giftcard: ['pending', 'sent', 'resent', 'delivered'],
  document: ['awaiting_docs', 'received', 'sent', 'accepted', 'rejected'],
};
const STATUS_META = {
  pending:       { es: 'Pendiente', en: 'Pending', cls: 'dkt-bdg-amb' },
  sent:          { es: 'Enviado', en: 'Sent', cls: 'dkt-bdg-cyn' },
  resent:        { es: 'Reenviado', en: 'Resent', cls: 'dkt-bdg-cyn' },
  delivered:     { es: 'Entregado', en: 'Delivered', cls: 'dkt-bdg-grn' },
  awaiting_docs: { es: 'Pendiente de recibir', en: 'Awaiting docs', cls: 'dkt-bdg-dim' },
  received:      { es: 'Recibido', en: 'Received', cls: 'dkt-bdg-cyn' },
  accepted:      { es: 'Aceptado', en: 'Accepted', cls: 'dkt-bdg-grn' },
  rejected:      { es: 'Rechazado', en: 'Rejected', cls: 'dkt-bdg-red' },
};
const stLabel = (s, es) => { const m = STATUS_META[s]; return m ? (es ? m.es : m.en) : s; };

// Resolve a status id to a label + color, checking the agency's custom statuses
// first, then the built-in catalogue. Custom statuses render with their own color.
function resolveStatus(s, customStatuses, es) {
  const custom = (customStatuses || []).find((x) => x && x.id === s);
  if (custom) return { label: custom.label, color: custom.color || '#64748b', custom: true };
  const m = STATUS_META[s];
  return { label: m ? (es ? m.es : m.en) : s, cls: m?.cls || 'dkt-bdg-dim', custom: false };
}
function FollowupStatusBadge({ status, customStatuses, es }) {
  const r = resolveStatus(status, customStatuses, es);
  if (r.custom) return <span className="dkt-bdg" style={{ background: `${r.color}22`, color: r.color, border: `1px solid ${r.color}55` }}>{r.label}</span>;
  return <span className={'dkt-bdg ' + r.cls}>{r.label}</span>;
}

// ── overdue / closed (badge uses STORED followups only — see deskte-full) ──
export const isFollowupClosed = (f) =>
  !!f && (f.status === 'delivered' || f.status === 'accepted');
export const isFollowupOverdue = (f) => {
  if (!f || isFollowupClosed(f)) return false;
  if (f.status === 'rejected') return true;
  if (f.dueAt) return new Date(f.dueAt).getTime() < Date.now();
  const base = f.sentAt || f.createdAt;
  if (!base) return false;
  return Date.now() - new Date(base).getTime() >= 7 * DAY;
};
export const countOverdueFollowups = (arr) => (arr || []).filter(isFollowupOverdue).length;

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
};
const toDateInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const nameOf = (c) => c ? `${c.firstName || ''} ${c.lastName || ''}`.trim() : '';

const blankForm = (kind, lockedClientId) => ({
  kind,
  status: kind === 'giftcard' ? 'pending' : 'awaiting_docs',
  title: '', amount: '', method: '', destination: '', caseRef: '',
  clientId: lockedClientId || '', clientName: '',
  referrerClientId: '', referrerName: '',
  sentAt: '', resentAt: '', respondedAt: '', dueAt: '', notes: '',
});

export function FollowupsView({
  followups = [],
  onFollowupsChange,
  clients = [],
  requests = [],
  currentUser,
  lang,
  canEdit = true,
  clientId = null,
  embedded = false,
}) {
  const es = lang === 'es';
  const [kindTab, setKindTab] = useState('all');
  const [statusFilter, setStatusFilter] = useState('open');
  const [q, setQ] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // row being edited, or null for new
  const [form, setForm] = useState(() => blankForm('document', clientId));
  // Agency custom statuses + columns (#3 Lissette 2026-06-29: table view with
  // custom columns + statuses). Owners manage them via the two modals below.
  const [customStatuses, persistFuStatuses] = useFollowupStatuses();
  const [customColumns, persistFuColumns] = useFollowupColumns();
  const [showStatusMgr, setShowStatusMgr] = useState(false);
  const [showColMgr, setShowColMgr] = useState(false);
  const [newStatusLabel, setNewStatusLabel] = useState('');
  const [newStatusColor, setNewStatusColor] = useState('#0ea5e9');
  const addCustomStatus = () => { const label = newStatusLabel.trim(); if (!label) return; persistFuStatuses([...(customStatuses || []), { id: `fs_${Math.random().toString(36).slice(2, 8)}`, label, color: newStatusColor }]); setNewStatusLabel(''); setNewStatusColor('#0ea5e9'); };
  const removeCustomStatus = (id) => persistFuStatuses((customStatuses || []).filter((s) => s.id !== id));
  // Status options for a given kind: built-ins for that kind + all custom ones.
  const statusOptionsFor = (kind) => [...(STATUS[kind] || STATUS.document), ...(customStatuses || []).filter((s) => s && s.id && s.label).map((s) => s.id)];

  // Overlays: stored followups linked to an auto source. Map by "type:id".
  const overlayMap = useMemo(() => {
    const m = {};
    (followups || []).forEach((f) => { if (f.sourceType && f.sourceId) m[`${f.sourceType}:${f.sourceId}`] = f; });
    return m;
  }, [followups]);
  // Pure-manual rows (no source).
  const manualRows = useMemo(() => (followups || []).filter((f) => !f.sourceType), [followups]);

  // 🎁 auto rows from referrals (clients that have a referrer).
  const autoGift = useMemo(() => {
    return (clients || [])
      .filter((c) => c && (c.referredByClientId || c.referredByFirstName || c.referredByLastName))
      .map((c) => {
        const refClient = c.referredByClientId ? (clients.find((x) => x.id === c.referredByClientId)) : null;
        const referrerName = refClient ? nameOf(refClient) : `${c.referredByFirstName || ''} ${c.referredByLastName || ''}`.trim();
        if (!referrerName) return null;
        const ov = overlayMap[`referral:${c.id}`];
        return {
          id: ov?.id || `auto-ref-${c.id}`, auto: true, overlay: ov || null,
          kind: 'giftcard', sourceType: 'referral', sourceId: c.id,
          clientId: c.id, clientName: nameOf(c),
          referrerClientId: c.referredByClientId || '', referrerName,
          status: ov?.status || 'pending',
          amount: ov?.amount ?? null, method: ov?.method || '',
          sentAt: ov?.sentAt || null, resentAt: ov?.resentAt || null, respondedAt: ov?.respondedAt || null,
          dueAt: ov?.dueAt || null, notes: ov?.notes || '',
          customData: ov?.customData || {},
          createdAt: ov?.createdAt || c.createdAt || null,
        };
      })
      .filter(Boolean);
  }, [clients, overlayMap]);

  // 📄 auto rows from document requests.
  const autoDoc = useMemo(() => {
    return (requests || []).map((r) => {
      const ov = overlayMap[`docrequest:${r.id}`];
      const received = r.status === 'completed';
      const partial = r.status === 'partial';
      // Displayed status: the user's overlay wins once they advance it past the
      // received stage; otherwise auto-reflect the request's upload state.
      const ovS = ov?.status;
      const status = ovS && ovS !== 'awaiting_docs' ? ovS : (received ? 'received' : 'awaiting_docs');
      return {
        id: ov?.id || `auto-req-${r.id}`, auto: true, overlay: ov || null,
        kind: 'document', sourceType: 'docrequest', sourceId: r.id,
        clientId: r.clientId || '', clientName: r.clientName || '',
        title: r.title || (r.documentsNeeded || []).join(', '),
        autoReceived: received, partial, requestStatus: r.status,
        status,
        destination: ov?.destination || '', caseRef: ov?.caseRef || '',
        sentAt: ov?.sentAt || null, respondedAt: ov?.respondedAt || null,
        dueAt: ov?.dueAt || null, notes: ov?.notes || '',
        customData: ov?.customData || {},
        createdAt: ov?.createdAt || r.createdAt || null,
      };
    });
  }, [requests, overlayMap]);

  const allRows = useMemo(() => {
    const gift = [...autoGift, ...manualRows.filter((f) => f.kind === 'giftcard')];
    const doc = [...autoDoc, ...manualRows.filter((f) => f.kind === 'document')];
    let rows = [...gift, ...doc];
    if (clientId) rows = rows.filter((r) => r.clientId === clientId || r.referrerClientId === clientId);
    return rows;
  }, [autoGift, autoDoc, manualRows, clientId]);

  const filtered = useMemo(() => {
    let rows = allRows;
    if (kindTab !== 'all') rows = rows.filter((r) => r.kind === kindTab);
    if (statusFilter === 'open') rows = rows.filter((r) => !isFollowupClosed(r));
    else if (statusFilter === 'todo') rows = rows.filter((r) => r.kind === 'giftcard' ? r.status === 'pending' : (r.status === 'received' || r.status === 'rejected'));
    else if (statusFilter !== 'all') rows = rows.filter((r) => r.status === statusFilter);
    const term = q.trim().toLowerCase();
    if (term) {
      rows = rows.filter((r) =>
        [r.clientName, r.referrerName, r.title, r.destination, r.caseRef, r.notes]
          .filter(Boolean).join(' ').toLowerCase().includes(term));
    }
    return rows.slice().sort((a, b) => {
      const ca = isFollowupClosed(a) ? 1 : 0, cb = isFollowupClosed(b) ? 1 : 0;
      if (ca !== cb) return ca - cb;
      return String(b.createdAt || '').localeCompare(String(a.createdAt || ''));
    });
  }, [allRows, kindTab, statusFilter, q]);

  const openCount = useMemo(() => allRows.filter((r) => !isFollowupClosed(r)).length, [allRows]);

  // Lo que ofrece cada columna de tipo LISTA: lo que la agencia ya escribio en
  // ella (Lissette 2026-09-10). Sale de TODAS las filas y no de las filtradas —
  // un filtro puesto no puede recortar lo que se puede elegir.
  const opcionesPorColumna = useMemo(() => {
    const m = {};
    for (const col of customColumns) {
      if (tipoDeColumna(col) !== 'list') continue;
      m[col.id] = opcionesDeColumna(col, allRows.map((r) => r.customData && r.customData[col.id]));
    }
    return m;
  }, [customColumns, allRows]);

  // Create-or-update the stored followup behind a row.
  const persist = (row, patch) => {
    if (row.overlay) {
      onFollowupsChange((prev) => prev.map((f) => (f.id === row.overlay.id ? { ...f, ...patch } : f)));
    } else if (!row.auto) {
      onFollowupsChange((prev) => prev.map((f) => (f.id === row.id ? { ...f, ...patch } : f)));
    } else {
      const id = 'fu_' + Math.random().toString(36).slice(2, 10);
      const base = {
        id, kind: row.kind, sourceType: row.sourceType, sourceId: row.sourceId,
        clientId: row.clientId, clientName: row.clientName,
        referrerClientId: row.referrerClientId || '', referrerName: row.referrerName || '',
        status: row.status, amount: row.amount ?? null, method: row.method || '',
        destination: row.destination || '', caseRef: row.caseRef || '',
        customData: row.customData || {},
        createdAt: new Date().toISOString(),
      };
      onFollowupsChange((prev) => [{ ...base, ...patch }, ...prev]);
    }
  };

  const changeStatus = (row, s) => {
    const patch = { status: s };
    const ts = new Date().toISOString();
    if (s === 'sent' && !row.sentAt) patch.sentAt = ts;
    if (s === 'resent') patch.resentAt = ts;
    if ((s === 'delivered' || s === 'accepted' || s === 'rejected') && !row.respondedAt) patch.respondedAt = ts;
    persist(row, patch);
  };

  const openAdd = (kind) => {
    setEditing(null);
    setForm({ ...blankForm(kind || (kindTab === 'giftcard' ? 'giftcard' : 'document'), clientId), customData: {} });
    setShowForm(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      kind: row.kind, status: row.status,
      title: row.title || '', amount: row.amount != null ? String(row.amount) : '', method: row.method || '',
      destination: row.destination || '', caseRef: row.caseRef || '',
      clientId: row.clientId || '', clientName: row.clientName || '',
      referrerClientId: row.referrerClientId || '', referrerName: row.referrerName || '',
      sentAt: toDateInput(row.sentAt), resentAt: toDateInput(row.resentAt),
      respondedAt: toDateInput(row.respondedAt), dueAt: toDateInput(row.dueAt), notes: row.notes || '',
      customData: { ...(row.customData || {}) },
    });
    setShowForm(true);
  };

  const submit = () => {
    const subject = clients.find((c) => c.id === form.clientId);
    const ref = clients.find((c) => c.id === form.referrerClientId);
    const data = {
      status: form.status, title: form.title, method: form.method,
      destination: form.destination, caseRef: form.caseRef,
      amount: form.amount === '' ? null : Number(form.amount),
      sentAt: form.sentAt || null, resentAt: form.resentAt || null,
      respondedAt: form.respondedAt || null, dueAt: form.dueAt || null, notes: form.notes,
      clientId: form.clientId, clientName: subject ? nameOf(subject) : form.clientName,
      referrerClientId: form.referrerClientId, referrerName: ref ? nameOf(ref) : form.referrerName,
      customData: form.customData || {},
    };
    if (!editing) {
      const id = 'fu_' + Math.random().toString(36).slice(2, 10);
      onFollowupsChange((prev) => [{ ...data, id, kind: form.kind, createdAt: new Date().toISOString() }, ...prev]);
    } else if (editing.auto) {
      persist(editing, data);
    } else {
      onFollowupsChange((prev) => prev.map((f) => (f.id === editing.id ? { ...f, ...data } : f)));
    }
    toast.success(es ? 'Guardado ✓' : 'Saved ✓');
    setShowForm(false);
  };

  const remove = async (row) => {
    const targetId = row.overlay ? row.overlay.id : row.id;
    const ok = await confirmDialog({
      title: es ? 'Eliminar registro' : 'Delete record',
      message: row.overlay
        ? (es ? 'Esto borra el estado guardado; la fila volverá a su valor automático.' : 'This clears the saved status; the row reverts to its automatic value.')
        : (es ? '¿Eliminar este seguimiento manual?' : 'Delete this manual follow-up?'),
      danger: true, confirmLabel: es ? 'Eliminar' : 'Delete',
    });
    if (!ok) return;
    onFollowupsChange((prev) => prev.filter((x) => x.id !== targetId));
  };

  // ── Seleccionar varios y borrar (Lissette 2026-08-20) ─────────────────────
  //
  // "en el modulo de follow-up no existe una manera para seleccionar todo y
  // borrar o seleccionar varias y borrar". Mismo patron que ya hay en Correos
  // Programados y en Prospectos, para que se comporte igual en los tres.
  //
  // Lo que NO se puede seleccionar: las filas automaticas sin estado guardado.
  // Esas no son un registro, son el reflejo de una solicitud de documento — no
  // hay nada que borrar, y borrarlas "funcionaria" solo hasta que la pantalla
  // se recalcula y vuelven a salir. Es la misma condicion que decide si esa
  // fila enseña su boton de borrar, escrita una sola vez para que no puedan
  // separarse.
  const sePuedeBorrar = (r) => canEdit && (r.overlay || !r.auto);
  const [selMode, setSelMode] = useState(false);
  const [selIds, setSelIds] = useState(() => new Set());
  // "Seleccionar todo" respeta el filtro y la busqueda que haya puestos: lo que
  // se ve es lo que se marca. Marcar en silencio filas que no estan en pantalla
  // es como se borra de mas.
  const borrables = useMemo(() => filtered.filter(sePuedeBorrar).map((r) => (r.overlay ? r.overlay.id : r.id)), [filtered, canEdit]);
  const todosSel = borrables.length > 0 && borrables.every((id) => selIds.has(id));
  const alternarSel = (id) => setSelIds((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const alternarTodos = () => setSelIds(todosSel ? new Set() : new Set(borrables));
  const salirSel = () => { setSelMode(false); setSelIds(new Set()); };

  const borrarSeleccionados = async () => {
    const ids = borrables.filter((id) => selIds.has(id));
    if (!ids.length) return;
    const ok = await confirmDialog({
      title: es ? 'Eliminar seleccionados' : 'Delete selected',
      message: es
        ? `Se eliminaran ${ids.length} registro(s). Las filas automaticas volveran a su valor calculado. Esto no se puede deshacer.`
        : `${ids.length} record(s) will be deleted. Automatic rows revert to their calculated value. This can't be undone.`,
      danger: true, confirmLabel: es ? `Eliminar ${ids.length}` : `Delete ${ids.length}`,
    });
    if (!ok) return;
    const fuera = new Set(ids);
    onFollowupsChange((prev) => prev.filter((x) => !fuera.has(x.id)));
    toast.success(es ? `${ids.length} eliminado(s) ✓` : `${ids.length} deleted ✓`);
    salirSel();
  };

  const isGift = form.kind === 'giftcard';
  const identityLocked = !!(editing && editing.auto); // can't change who/what on auto rows

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {!embedded && (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <div className="dkt-sec-title">{es ? 'Seguimiento / Envíos' : 'Follow-ups / Sent items'}</div>
            <div className="dkt-sec-sub" style={{ display: 'flex', alignItems: 'center', gap: '.35rem' }}>
              <Zap size={12} /> {es ? 'Se llena solo con tus referidos y solicitudes de documentos' : 'Auto-filled from your referrals and document requests'}
            </div>
          </div>
          {canEdit && (
            <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
              <button type="button" className="dkt-btn ghost" onClick={() => setShowStatusMgr(true)}><Flag size={14} /> {es ? 'Estados' : 'Statuses'}</button>
              <button type="button" className="dkt-btn ghost" onClick={() => setShowColMgr(true)}><ColumnsIcon size={14} /> {es ? 'Columnas' : 'Columns'}</button>
              <button type="button" className="dkt-btn ghost" onClick={() => openAdd('giftcard')}><Gift size={15} /> {es ? 'Gift Card manual' : 'Manual gift card'}</button>
              <button type="button" className="dkt-btn primary" onClick={() => openAdd('document')}><Plus size={15} /> {es ? 'Documento manual' : 'Manual document'}</button>
            </div>
          )}
        </div>
      )}

      {embedded && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.5rem' }}>
          <div style={{ fontWeight: 700, fontSize: '.9rem', color: 'var(--c-white,#eef6ff)', display: 'flex', alignItems: 'center', gap: '.4rem' }}>
            <Send size={15} /> {es ? 'Seguimiento / Envíos' : 'Follow-ups / Sent items'}
          </div>
          {canEdit && (
            <div style={{ display: 'flex', gap: '.4rem' }}>
              <button type="button" className="dkt-btn ghost xs" onClick={() => openAdd('giftcard')}><Gift size={13} /> Gift</button>
              <button type="button" className="dkt-btn ghost xs" onClick={() => openAdd('document')}><FileText size={13} /> Doc</button>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        {[{ id: 'all', label: es ? 'Todos' : 'All' }, { id: 'giftcard', label: '🎁 Gift Cards' }, { id: 'document', label: '📄 Documentos' }].map((tb) => (
          <button key={tb.id} type="button" className={'dkt-tab' + (kindTab === tb.id ? ' on' : '')} onClick={() => setKindTab(tb.id)}>{tb.label}</button>
        ))}
        <select className="dkt-inp" style={{ width: 'auto', padding: '.38rem .6rem', fontSize: '.75rem' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="open">{es ? 'Abiertos' : 'Open'}</option>
          <option value="todo">{es ? '⚡ Por hacer' : '⚡ To do'}</option>
          <option value="all">{es ? 'Todos los estados' : 'All statuses'}</option>
          <option value="delivered">{es ? 'Entregados' : 'Delivered'}</option>
          <option value="accepted">{es ? 'Aceptados' : 'Accepted'}</option>
          <option value="rejected">{es ? 'Rechazados' : 'Rejected'}</option>
        </select>
        {!embedded && <input className="dkt-search" style={{ flex: 1, minWidth: 160, maxWidth: 320 }} placeholder={es ? '🔍 Buscar…' : '🔍 Search…'} value={q} onChange={(e) => setQ(e.target.value)} />}
        <span style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', marginLeft: 'auto' }} className="dkt-mono">{openCount} {es ? 'abiertos' : 'open'}</span>

        {borrables.length > 0 && (!selMode ? (
          <button type="button" className="dkt-btn ghost xs" onClick={() => setSelMode(true)}>
            {'\u2611\uFE0F '}{es ? 'Seleccionar' : 'Select'}
          </button>
        ) : (
          <>
            <button type="button" className="dkt-btn ghost xs" onClick={alternarTodos}>
              {todosSel ? (es ? 'Quitar todo' : 'Clear all') : (es ? 'Seleccionar todo' : 'Select all')}
            </button>
            <button type="button" className="dkt-btn xs" disabled={selIds.size === 0} onClick={borrarSeleccionados}
              style={{ background: selIds.size ? 'var(--c-red,#ff3d6b)' : undefined, borderColor: 'transparent', color: selIds.size ? '#fff' : undefined }}>
              {es ? 'Eliminar' : 'Delete'} ({selIds.size})
            </button>
            <button type="button" className="dkt-btn ghost xs" onClick={salirSel}>{es ? 'Salir' : 'Done'}</button>
          </>
        ))}      </div>

      {/* List — TABLE view (Lissette 2026-06-29: columns, not cards) */}
      {filtered.length === 0 ? (
        <div className="dkt-card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--c-text3,#45688a)', fontSize: '.85rem' }}>
          {es ? 'Nada por aquí. Aparecerá solo cuando registres referidos o solicitudes de documentos.' : 'Nothing here. Rows appear automatically as you add referrals or document requests.'}
        </div>
      ) : (
        <div className="dkt-card" style={{ overflowX: 'auto', padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead><tr>
              {selMode && <th style={{ ...fuTh, width: 30 }}></th>}
              <th style={fuTh}>{es ? 'Tipo' : 'Type'}</th>
              <th style={fuTh}>{es ? 'Cliente' : 'Client'}</th>
              <th style={fuTh}>{es ? 'Referidor' : 'Referrer'}</th>
              <th style={fuTh}>{es ? 'Detalle' : 'Detail'}</th>
              <th style={fuTh}>{es ? 'Recibido' : 'Received'}</th>
              <th style={fuTh}>{es ? 'Destino / Método' : 'Dest / Method'}</th>
              <th style={fuTh}>{es ? 'Monto' : 'Amount'}</th>
              <th style={fuTh}>{es ? 'Estado' : 'Status'}</th>
              <th style={fuTh}>{es ? 'Enviado' : 'Sent'}</th>
              <th style={fuTh}>{es ? 'Vence' : 'Due'}</th>
              <th style={fuTh}>{es ? 'Notas' : 'Notes'}</th>
              {customColumns.map((col) => <th key={col.id} style={fuTh}>{col.name}</th>)}
              <th style={fuTh}></th>
            </tr></thead>
            <tbody>
              {filtered.map((r) => {
                const overdue = isFollowupOverdue(r.overlay || (r.auto ? null : r));
                const gift = r.kind === 'giftcard';
                const received = r.autoReceived || ['received', 'sent', 'accepted', 'rejected'].includes(r.status);
                const dash = <span style={{ color: 'var(--c-text3,#45688a)' }}>—</span>;
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid var(--c-border-soft,rgba(26,58,94,.5))' }}>
                    {selMode && (
                      <td style={{ ...fuTd, width: 30 }}>
                        {sePuedeBorrar(r) && (
                          <input type="checkbox"
                            checked={selIds.has(r.overlay ? r.overlay.id : r.id)}
                            onChange={() => alternarSel(r.overlay ? r.overlay.id : r.id)} />
                        )}
                      </td>
                    )}
                    <td style={fuTd}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>{gift ? <Gift size={12} /> : <FileText size={12} />} {gift ? 'Gift' : (es ? 'Doc' : 'Doc')}</span>
                      {r.auto && <span className="dkt-bdg dkt-bdg-cyn" style={{ marginLeft: 5, gap: 2 }} title={es ? 'Automático' : 'Auto'}><Zap size={9} /></span>}
                      {overdue && <span className="dkt-bdg dkt-bdg-red" style={{ marginLeft: 5 }} title={es ? 'Vencido' : 'Overdue'}><AlertTriangle size={9} /></span>}
                    </td>
                    <td style={{ ...fuTd, color: 'var(--c-white,#eef6ff)', fontWeight: 600 }}>{r.clientName || dash}</td>
                    <td style={fuTd}>{gift ? (r.referrerName || dash) : dash}</td>
                    <td style={fuTd}>{!gift ? (r.title || dash) : (r.method ? dash : dash)}</td>
                    <td style={fuTd}>{gift ? dash : (received
                      ? <span style={{ color: 'var(--c-green,#00ff88)', display: 'inline-flex', alignItems: 'center', gap: 3 }}><Check size={12} /> {es ? 'Sí' : 'Yes'}</span>
                      : r.partial ? <span style={{ color: 'var(--c-amber,#ffb700)' }}>◑</span> : <span style={{ color: 'var(--c-text3,#45688a)' }}>○</span>)}</td>
                    <td style={fuTd}>{gift ? (r.method || dash) : (r.destination || dash)}{!gift && r.caseRef ? <span style={{ color: 'var(--c-text3,#45688a)' }}> · #{r.caseRef}</span> : null}</td>
                    <td style={fuTd}>{gift && r.amount != null ? `$${Number(r.amount).toFixed(2)}` : dash}</td>
                    <td style={fuTd}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <FollowupStatusBadge status={r.status} customStatuses={customStatuses} es={es} />
                        {canEdit && (
                          <select className="dkt-inp" style={{ width: 'auto', padding: '.2rem .35rem', fontSize: '.7rem' }} value={statusOptionsFor(r.kind).includes(r.status) ? r.status : r.status} onChange={(e) => changeStatus(r, e.target.value)}>
                            {statusOptionsFor(r.kind).map((s) => <option key={s} value={s}>{resolveStatus(s, customStatuses, es).label}</option>)}
                          </select>
                        )}
                      </div>
                    </td>
                    <td style={{ ...fuTd, whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', fontSize: '.72rem' }}>{r.sentAt ? fmtDate(r.sentAt) : dash}</td>
                    <td style={{ ...fuTd, whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', fontSize: '.72rem', color: overdue ? 'var(--c-red,#ff3d6b)' : undefined }}>{r.dueAt ? fmtDate(r.dueAt) : dash}</td>
                    <td style={{ ...fuTd, maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.notes || ''}>{r.notes ? <RichNote text={r.notes} style={{ whiteSpace: 'nowrap' }} /> : dash}</td>
                    {/* Una columna de marcar guarda un "1": pintarlo tal cual no
                        le dice nada a nadie. Se lee como casilla. */}
                    {customColumns.map((col) => <td key={col.id} style={fuTd}>{
                      tipoDeColumna(col) === 'check'
                        ? (estaMarcada(r.customData && r.customData[col.id]) ? '☑' : '☐')
                        : ((r.customData && r.customData[col.id]) || dash)
                    }</td>)}
                    <td style={{ ...fuTd, whiteSpace: 'nowrap' }}>
                      {canEdit && <button type="button" title={es ? 'Editar' : 'Edit'} onClick={() => openEdit(r)} style={iconBtn}><Pencil size={13} /></button>}
                      {sePuedeBorrar(r) && <button type="button" title={es ? 'Eliminar' : 'Delete'} onClick={() => remove(r)} style={{ ...iconBtn, marginLeft: 4 }}><Trash2 size={13} /></button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit modal */}
      {showForm && (
        <Modal title={(editing ? (es ? 'Editar ' : 'Edit ') : (es ? 'Nuevo ' : 'New ')) + (isGift ? 'gift card' : (es ? 'documento' : 'document'))} onClose={() => setShowForm(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.85rem' }}>
            {!editing && (
              <div style={{ display: 'flex', gap: '.5rem' }}>
                <button type="button" className={'dkt-tab' + (isGift ? ' on' : '')} onClick={() => setForm((f) => ({ ...f, kind: 'giftcard', status: 'pending' }))}><Gift size={13} /> {es ? 'Gift Card a referido' : 'Gift card to referrer'}</button>
                <button type="button" className={'dkt-tab' + (!isGift ? ' on' : '')} onClick={() => setForm((f) => ({ ...f, kind: 'document', status: 'awaiting_docs' }))}><FileText size={13} /> {es ? 'Documento' : 'Document'}</button>
              </div>
            )}

            {identityLocked ? (
              <div style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)', background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.55rem .7rem' }}>
                {isGift
                  ? <>🎁 <strong>{form.referrerName}</strong> {es ? '— por referir a ' : '— for referring '}<strong>{form.clientName}</strong></>
                  : <>📄 <strong>{form.clientName}</strong>{form.title ? ` — ${form.title}` : ''}</>}
                <div style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', marginTop: '.2rem' }}>{es ? 'Generado del sistema — edita el estado y los datos de envío abajo.' : 'From the system — edit status and shipping details below.'}</div>
              </div>
            ) : (
              <>
                <div>
                  <label className="dkt-input-lbl">{isGift ? (es ? 'Cliente referido' : 'Referred client') : (es ? 'Cliente' : 'Client')}</label>
                  <ClientSearchSelect clients={clients} value={form.clientId} onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))} lang={lang} />
                </div>
                {isGift && (
                  <div>
                    <label className="dkt-input-lbl">{es ? 'Quién refirió (cliente)' : 'Who referred (client)'}</label>
                    <ClientSearchSelect clients={clients} value={form.referrerClientId} onChange={(e) => setForm((f) => ({ ...f, referrerClientId: e.target.value }))} lang={lang} />
                  </div>
                )}
                {!isGift && (
                  <div>
                    <label className="dkt-input-lbl">{es ? 'Documento (ej. "Prueba de ingresos")' : 'Document (e.g. "Proof of income")'}</label>
                    <input className="dkt-inp" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
                  </div>
                )}
              </>
            )}

            {isGift ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
                <div>
                  <label className="dkt-input-lbl">{es ? 'Monto ($)' : 'Amount ($)'}</label>
                  <input className="dkt-inp" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
                </div>
                <div>
                  <label className="dkt-input-lbl">{es ? 'Método' : 'Method'}</label>
                  <select className="dkt-inp" value={form.method} onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))}>
                    <option value="">—</option>
                    <option value={es ? 'Correo' : 'Email'}>{es ? 'Correo' : 'Email'}</option>
                    <option value={es ? 'Físico' : 'Physical'}>{es ? 'Físico / correo postal' : 'Physical / mail'}</option>
                    <option value={es ? 'En persona' : 'In person'}>{es ? 'En persona' : 'In person'}</option>
                  </select>
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
                <div>
                  <label className="dkt-input-lbl">{es ? 'Enviado a (Marketplace/compañía)' : 'Sent to (Marketplace/carrier)'}</label>
                  <input className="dkt-inp" list="followup-dest" placeholder={es ? 'Marketplace o compañía' : 'Marketplace or carrier'} value={form.destination} onChange={(e) => setForm((f) => ({ ...f, destination: e.target.value }))} />
                  <datalist id="followup-dest">
                    <option value="Marketplace" /><option value="Florida Blue" /><option value="Ambetter" /><option value="Oscar" /><option value="Aetna" /><option value="Cigna" /><option value="UnitedHealthcare" /><option value="Molina" />
                  </datalist>
                </div>
                <div>
                  <label className="dkt-input-lbl">{es ? 'Nº de caso / referencia' : 'Case / reference #'}</label>
                  <input className="dkt-inp" value={form.caseRef} onChange={(e) => setForm((f) => ({ ...f, caseRef: e.target.value }))} />
                </div>
              </div>
            )}

            <div>
              <label className="dkt-input-lbl">{es ? 'Estado' : 'Status'}</label>
              <select className="dkt-inp" value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                {statusOptionsFor(form.kind).map((s) => <option key={s} value={s}>{resolveStatus(s, customStatuses, es).label}</option>)}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
              <div><label className="dkt-input-lbl">{es ? 'Fecha enviado' : 'Sent date'}</label><input className="dkt-inp" type="date" value={form.sentAt} onChange={(e) => setForm((f) => ({ ...f, sentAt: e.target.value }))} /></div>
              {isGift && <div><label className="dkt-input-lbl">{es ? 'Fecha reenviado' : 'Resent date'}</label><input className="dkt-inp" type="date" value={form.resentAt} onChange={(e) => setForm((f) => ({ ...f, resentAt: e.target.value }))} /></div>}
              <div><label className="dkt-input-lbl">{es ? 'Fecha respuesta' : 'Response date'}</label><input className="dkt-inp" type="date" value={form.respondedAt} onChange={(e) => setForm((f) => ({ ...f, respondedAt: e.target.value }))} /></div>
              <div><label className="dkt-input-lbl">{es ? 'Recordarme el' : 'Remind me on'}</label><input className="dkt-inp" type="date" value={form.dueAt} onChange={(e) => setForm((f) => ({ ...f, dueAt: e.target.value }))} /></div>
            </div>

            <div><label className="dkt-input-lbl">{es ? 'Notas' : 'Notes'}</label><NoteEditor lang={es ? 'es' : 'en'} rows={2} value={form.notes} onChange={(v) => setForm((f) => ({ ...f, notes: v }))} /></div>

            {customColumns.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
                {customColumns.map((col) => (
                  <div key={col.id}>
                    <label className="dkt-input-lbl">{col.name}</label>
                    <CampoDeColumnaPropia columna={col} lang={lang} opciones={opcionesPorColumna[col.id] || []}
                      valor={(form.customData && form.customData[col.id]) || ''}
                      onChange={(v) => setForm((f) => ({ ...f, customData: { ...(f.customData || {}), [col.id]: v } }))} />
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.5rem' }}>
              <button type="button" className="dkt-btn ghost" onClick={() => setShowForm(false)}>{es ? 'Cancelar' : 'Cancel'}</button>
              <button type="button" className="dkt-btn primary" onClick={submit}>{es ? 'Guardar' : 'Save'}</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Custom statuses manager */}
      {showStatusMgr && (
        <Modal title={es ? 'Estados personalizados' : 'Custom statuses'} onClose={() => setShowStatusMgr(false)}>
          <p style={{ fontSize: '.8rem', color: 'var(--c-text2,#7aa3c0)', marginBottom: 12 }}>{es ? 'Los estados base no se pueden borrar. Agrega los tuyos con color — aparecen en el selector de estado de cada fila.' : 'Built-in statuses can\'t be removed. Add your own with a color — they show in each row\'s status selector.'}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
            {(customStatuses || []).map((s) => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.82rem', color: 'var(--c-text,#c8dff0)' }}>
                <span style={{ width: 10, height: 10, borderRadius: 99, background: s.color || '#64748b' }} />{s.label}
                <button onClick={() => removeCustomStatus(s.id)} style={{ marginLeft: 'auto', fontSize: '.7rem', color: 'var(--c-red,#ff3d6b)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>{es ? 'Quitar' : 'Remove'}</button>
              </div>
            ))}
            {(customStatuses || []).length === 0 && <div style={{ fontSize: '.76rem', color: 'var(--c-text2,#7aa3c0)', fontStyle: 'italic' }}>{es ? 'Aún no hay estados personalizados.' : 'No custom statuses yet.'}</div>}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="color" value={newStatusColor} onChange={(e) => setNewStatusColor(e.target.value)} style={{ width: 38, height: 38, padding: 0, border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, cursor: 'pointer', background: 'var(--c-surface2,#112538)' }} />
            <input value={newStatusLabel} onChange={(e) => setNewStatusLabel(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addCustomStatus(); }} placeholder={es ? 'Nombre del estado' : 'Status name'} style={{ flex: 1, fontSize: '.84rem', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface2,#112538)', color: 'var(--c-text,#c8dff0)' }} />
            <button onClick={addCustomStatus} className="dkt-btn primary">{es ? 'Agregar' : 'Add'}</button>
          </div>
        </Modal>
      )}

      {/* Custom columns manager (shared component, also used by Vault/Company —
          it renders its own Modal, so don't wrap it in another one). */}
      {showColMgr && (
        <CustomColumnsManager columns={customColumns} lang={lang}
          title={es ? 'Columnas de Seguimiento' : 'Follow-up columns'}
          onClose={() => setShowColMgr(false)} onSave={persistFuColumns} />
      )}
    </div>
  );
}

const fuTh = { textAlign: 'left', padding: '.45rem .6rem', fontSize: '.62rem', fontWeight: 700, color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.06em', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', borderBottom: '1px solid var(--c-border,#1a3550)' };
const fuTd = { padding: '.5rem .6rem', fontSize: '.78rem', color: 'var(--c-text,#c8dff0)', verticalAlign: 'middle' };

const iconBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, borderRadius: 7,
  border: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface2,#112538)',
  color: 'var(--c-text2,#7aa3c0)', cursor: 'pointer',
};
